import React, { useState, useEffect } from 'react';
import './styles.css';

const EvaluationForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    sportsHours: NaN,
    hobbies: NaN,
    graduationClass: NaN,
    classTeacher: NaN,
    workHours: NaN,
    residence: NaN,
    category: NaN,
    gender: NaN,
    age: NaN,
    maritalStatus: NaN,
    income: NaN
  });

  const [score, setScore] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const epilogue = '为了有效减轻职业紧张并提升工作满意度，我们建议教师们定期参与心理健康培训，建立同事间的支持网络，利用在线资源获取即时支持，同时倡导积极的校园文化。自我关照也非常重要，包括定期自我检查和通过运动、冥想等放松技巧来管理压力。当自我调节无效时，不要犹豫寻求专业心理咨询。此外，定期进行体育锻炼和培养业余爱好也是缓解压力、提升生活质量的有效方式。通过这些综合措施，教师们可以更好地管理职业压力，享受更健康、更满意的职业生涯。'

  // success, info, warning, danger
  const colors = ['--color-success', '--color-info', '--color-warning', '--color-danger', '--color-danger'];
  const levels = ['无', '低', '中', '高', '极高'];

  const questions = [
    {
      id: 'category',
      title: '单位类别',
      type: 'single',
      options: [
        { value: 1, label: '小学' },
        { value: 2, label: '中学' },
        { value: 3, label: '高中' }
      ]
    },
    {
      id: 'gender',
      title: '性别',
      type: 'single',
      options: [
        { value: 1, label: '男' },
        { value: 2, label: '女' }
      ]
    },
    {
      id: 'age',
      title: '年龄',
      type: 'range',
      options: [
        { value: 3, label: '20-35岁' },
        { value: 2, label: '36-45岁' },
        { value: 1, label: '46岁以上' }
      ]
    },
    {
      id: 'income',
      title: '月收入',
      type: 'range',
      options: [
        { value: 1, label: '5000元以下' },
        { value: 2, label: '5001-8000元' },
        { value: 3, label: '8000元以上' }
      ]
    },
    {
      id: 'maritalStatus',
      title: '婚姻情况',
      type: 'single',
      options: [
        { value: 2, label: '未婚' },
        { value: 1, label: '已婚' },
        { value: 3, label: '离异或丧偶' }
      ]
    },
    {
      id: 'residence',
      title: '户籍',
      type: 'single',
      options: [
        { value: 1, label: '本地（兰州市）' },
        { value: 3, label: '外地' }
      ]
    },
    {
      id: 'graduationClass',
      title: '带教毕业班',
      type: 'boolean',
      options: [
        { value: 1, label: '是' },
        { value: 2, label: '否' }
      ]
    },
    {
      id: 'classTeacher',
      title: '任教班主任',
      type: 'boolean',
      options: [
        { value: 1, label: '是' },
        { value: 2, label: '否' }
      ]
    },
    {
      id: 'workHours',
      title: '每周工作时长',
      type: 'range',
      options: [
        { value: 1, label: '40小时及以下' },
        { value: 3, label: '41-48小时' },
        { value: 4, label: '49-56小时' },
        { value: 5, label: '57小时以上' }
      ]
    },
    {
      id: 'sportsHours',
      title: '每周体育运动时间',
      type: 'range',
      options: [
        { value: 1, label: '从不' },
        { value: 2, label: '1-3次/月' },
        { value: 3, label: '1-3次/周' },
        { value: 4, label: '4-6次/周' },
        { value: 5, label: '每天' }
      ]
    },
    {
      id: 'hobbies',
      title: '业余爱好',
      type: 'range',
      options: [
        { value: 1, label: '文学' },
        { value: 2, label: '音乐' },
        { value: 3, label: '体育' },
        { value: 4, label: '影视' },
        { value: 5, label: '其他' }
      ]
    }
  ];

  const handleOptionSelect = (value) => {
    setFormData({
      ...formData,
      [questions[currentStep].id]: value
    });

    if (currentStep < questions.length - 1) {
      setTimeout(() => setCurrentStep(currentStep + 1), 360);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch('/api/evaluate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      setScore(data.prediction.toFixed(2));
      setFeedback(data.feedback || generateFeedback(data.prediction_rounded));
    } catch (error) {
      console.error('Error fetching evaluation result:', error);
      alert('评估请求失败，请稍后重试！');
    }
    setSubmitting(false);
  };

  const generateFeedback = (score) => {
    if (score === 1) {
      return '您的工作生活平衡状况良好，但仍有改善空间。';
    } else if (score === 2) {
      return '建议您多关注工作生活平衡，适当调整时间分配。';
    } else if (score === 3) {
      return '您的工作生活压力较大，建议及时调整，注意休息。';
    } else {
      return '您的工作生活平衡状况较差，建议立即调整工作安排，保持身心健康。';
    }
  };

  const currentQuestion = questions[currentStep];

  useEffect(() => {
    fetch('/api/', {
      method: 'GET',
    })
      .then((response) => response.json())
      .then((data) => console.log(data))
      .catch((error) => console.error('Error fetching data:', error));
  }, []);

  useEffect(() => {
    if (currentStep < questions.length - 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, questions.length]);

  return (
    <div className="questionnaire-container">
      <div className="progress-bar">
        <div
          className="progress-fill"
          style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
        />
      </div>

      <div className="question-card">
        <h1 className="question-title">{currentQuestion.title}</h1>

        <div className="options-container">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              className={`option-button ${formData[currentQuestion.id] === option.value ? 'selected' : ''}`}
              onClick={() => handleOptionSelect(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="navigation-buttons">
          <button
            className="nav-button"
            onClick={handlePrevious}
            disabled={currentStep === 0}
          >
            上一题
          </button>

          {currentStep === questions.length - 1 ? (
            <button
              className="nav-button submit-button"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? '评估中...' : '提交'}
            </button>
          ) : (
            <button
              className="nav-button"
              onClick={handleNext}
              disabled={isNaN(formData[currentQuestion.id])}
            >
              下一题
            </button>
          )}
        </div>
      </div>

      {score !== null && (
        <div className="result-card">
          <h2>评估结果</h2>
          <div
            className="score-display"
          >
            <p>职业紧张等级：
              <span
                style={{
                  color: `var(${colors[Math.round(score) - 1]})`,
                  fontWeight: 'bold',
                  fontSize: '2rem'
                }}>{levels[Math.round(score) - 1]}
              </span>
            </p>
          </div>
          <p className="feedback-text">{feedback}</p>
          <p className="epilogue-text">{epilogue}</p>
        </div>
      )}
    </div>
  );
};

export default EvaluationForm;
